/**
 * SEO landing-page configurations. Each entry maps a route path to the
 * page-specific H1, copy, and meta tags. Keep keyword phrases natural —
 * one focused phrase per page, repeated 3-5 times across H1 + intro + body.
 */
export const SEO_LANDINGS = {
  "lethbridge-indian-pizza": {
    slug: "lethbridge-indian-pizza",
    path: "/lethbridge-indian-pizza",
    eyebrow: "Indian Pizza · Lethbridge AB",
    h1: "The home of Indian pizza in Lethbridge.",
    title: "Indian Pizza Lethbridge | Rivaaz Indian Pizza & Cuisine",
    description:
      "Rivaaz serves Lethbridge's most original Indian pizza — tandoor-fired crust, butter chicken, paneer tikka, tandoori toppings. Order online or call (403) 942-2565.",
    keywords:
      "Indian pizza Lethbridge, butter chicken pizza Lethbridge, paneer pizza, tandoori pizza, Indian fusion pizza Alberta, Rivaaz pizza",
    intro:
      "Tandoor-fired crust. Butter chicken sauce. Paneer that snaps. Indian flavours, pizza format, made-to-order on Mayor Magrath Drive.",
    highlights: [
      {
        icon: "pizza",
        title: "10 fusion pizzas",
        body: "Butter chicken, paneer tikka, tandoori chicken, malai veggie, achari, and classic favourites — Small 10\", Medium 12\", Large 15\".",
      },
      {
        icon: "flame",
        title: "Made in the tandoor",
        body: "Crusts char-finished in our clay tandoor for a smoke you can't fake. Different from your usual takeout — and that's the point.",
      },
      {
        icon: "pin",
        title: "1020 Mayor Magrath Dr S",
        body: "Pick up in 25 minutes or take delivery in Lethbridge, Coaldale and Coalhurst (with valid postal code at checkout).",
      },
    ],
    bodyHeading: "Why Lethbridge keeps coming back for our Indian pizza.",
    body: `Most pizza in Lethbridge follows the same script — cheese, pepperoni, ranch on the side. We started Rivaaz to ask a different question: what happens when a tandoor meets a pizza oven, and butter chicken meets dough that's been resting overnight?

The answer is what's on the menu today. Our Butter Chicken Pizza is the menu's best-seller — house-made butter sauce reduced with whole spices, then layered with grilled chicken, mozzarella, bell pepper and cilantro on a tandoor-finished base. The Paneer Tikka Pizza brings smoky cottage cheese marinated in yogurt and chilli, while Malai Veggie is the lighter, herbier sibling for vegetarians.

If pizza isn't your thing today, our curry, tandoori grill and biryani sections run just as deep — 149 dishes across 16 categories. Spice levels are yours to pick at checkout (Mild, Medium, Spicy, Extra Spicy), and combo "Feasts" let you build a family-sized meal in one flow.

Pickup is ready in about 25 minutes. Delivery is in-house — no app commission, no marked-up prices. Just call (403) 942-2565 or order online below.`,
  },

  "best-curry-lethbridge": {
    slug: "best-curry-lethbridge",
    path: "/best-curry-lethbridge",
    eyebrow: "Indian Curry · Lethbridge AB",
    h1: "Slow-simmered Indian curry in Lethbridge.",
    title: "Best Indian Curry Lethbridge | Butter Chicken, Korma, Vindaloo | Rivaaz",
    description:
      "Rivaaz Indian Pizza & Cuisine cooks Lethbridge's most loved Indian curries — butter chicken, lamb korma, fish curry, daal makhani, paneer butter masala. Order online for pickup or delivery.",
    keywords:
      "best curry Lethbridge, butter chicken Lethbridge, lamb curry, Indian food Lethbridge, daal makhani, vindaloo, paneer Lethbridge",
    intro:
      "Whole spices toasted from scratch. Tomato-and-cream gravies simmered for hours. Choose your spice level and we'll cook to it — Mild to Extra Spicy.",
    highlights: [
      {
        icon: "soup",
        title: "30+ curry varieties",
        body: "Butter chicken, korma, vindaloo, madras, jalfrezi, saag, kadhai, do-pyaza, makhani and more — chicken, lamb, beef, fish, paneer or veggie.",
      },
      {
        icon: "flame",
        title: "You pick the spice",
        body: "Mild (no heat, kid-friendly), Medium (warm, default), Spicy (how most regulars order), Extra Spicy (bring water).",
      },
      {
        icon: "clock",
        title: "Ready in 25 minutes",
        body: "Most pickup orders are out in 20–30 minutes. Add a tandoori naan or rice — we'll time it so everything's hot at the same time.",
      },
    ],
    bodyHeading: "What makes a Rivaaz curry different from the rest.",
    body: `Real Indian curry isn't a single sauce poured over protein — it's a layered build of toasted whole spices, slow-rendered onion-tomato base, ginger-garlic paste cooked until it stops smelling raw, then finishing cream, butter or coconut depending on the dish.

That's how we cook every curry at Rivaaz. Our Butter Chicken uses boneless chicken breast marinated overnight in ginger and yogurt, then grilled and folded into a tomato-cream gravy reduced with cardamom, clove and fenugreek leaf. Lamb Korma is the opposite of fast — cubed lamb braised low and slow with cashew, onion and royal spices until it falls apart under a fork.

For vegetarians, Daal Makhani is the dish we're proud of — black lentils and kidney beans simmered overnight with butter and cream until they're impossibly smooth. Paneer Butter Masala, Chana Masala, Aloo Gobi, Saag Paneer and Malai Kofta round out the side.

Every curry is built to your spice tolerance at checkout. Pair with tandoori naan, jeera rice or basmati — and if you can't decide, our combo Feasts let you taste five or six dishes in one order.`,
  },

  "halal-food-lethbridge": {
    slug: "halal-food-lethbridge",
    path: "/halal-food-lethbridge",
    eyebrow: "Halal · Lethbridge AB",
    h1: "Halal Indian food in Lethbridge.",
    title: "Halal Indian Food Lethbridge | Rivaaz Indian Pizza & Cuisine",
    description:
      "All chicken, lamb, beef and goat at Rivaaz Indian Pizza & Cuisine is halal-certified. Order halal butter chicken, biryani, tandoori, lamb curry online — pickup & delivery in Lethbridge AB.",
    keywords:
      "halal food Lethbridge, halal Indian restaurant, halal pizza Lethbridge, halal butter chicken, halal lamb curry, halal biryani Alberta",
    intro:
      "Every chicken, lamb, beef and goat dish on our menu is prepared with halal-certified meat. Eat with confidence — fusion pizza, curry, tandoori, biryani.",
    highlights: [
      {
        icon: "flame",
        title: "Halal-certified meat",
        body: "We source from a halal-certified supplier. Chicken, lamb, beef and goat are all halal.",
      },
      {
        icon: "pizza",
        title: "Halal pizza too",
        body: "Most pizzas in Lethbridge aren't halal. Ours are — including the popular butter chicken and tandoori chicken pizzas.",
      },
      {
        icon: "pin",
        title: "Order online or visit",
        body: "1020 Mayor Magrath Drive South. Pickup ready in ~25 min, in-house delivery to Lethbridge, Coaldale & Coalhurst.",
      },
    ],
    bodyHeading: "Halal options across the entire Rivaaz menu.",
    body: `Finding halal Indian food in Lethbridge can feel like a quiet game of "I'll have to ask the kitchen." At Rivaaz we want it to be the opposite — every meat dish you see on our menu is already halal. No asking, no exceptions.

That includes our full tandoori range (tandoori chicken, malai tikka, lamb seekh kebab), every chicken and lamb curry (butter chicken, korma, vindaloo, kadhai, saag), our biryanis, and our Indian fusion pizzas with chicken or lamb toppings. Vegetarians and vegans also have a deep menu — paneer, daal, chana, mixed vegetable and aloo gobi options.

If you have specific dietary needs beyond halal — gluten-free, nut allergy, dairy-free — just leave a note at checkout or call us before placing the order. Many of our curries are naturally gluten-free and can be made dairy-free with coconut cream substitutions.`,
  },

  "vegetarian-indian-lethbridge": {
    slug: "vegetarian-indian-lethbridge",
    path: "/vegetarian-indian-lethbridge",
    eyebrow: "Vegetarian · Lethbridge AB",
    h1: "Vegetarian Indian food in Lethbridge.",
    title: "Vegetarian Indian Food Lethbridge | Paneer, Daal, Veggie Pizza | Rivaaz",
    description:
      "40+ vegetarian Indian dishes at Rivaaz Lethbridge — paneer butter masala, daal makhani, chana masala, malai veggie pizza, samosas. Order online for pickup or delivery.",
    keywords:
      "vegetarian Indian Lethbridge, paneer Lethbridge, vegan curry, daal makhani, samosa Lethbridge, veggie pizza Lethbridge",
    intro:
      "Vegetarians don't get the leftover end of the menu here. 40+ vegetarian dishes, from paneer butter masala to malai veggie pizza to fresh-pulled naan.",
    highlights: [
      {
        icon: "soup",
        title: "Curry classics",
        body: "Daal makhani, chana masala, paneer butter masala, palak paneer, malai kofta, aloo gobi, kadhai paneer, baingan bharta.",
      },
      {
        icon: "pizza",
        title: "Vegetarian pizzas",
        body: "Malai veggie, paneer tikka, achari paneer, BBQ paneer — all on our tandoor-finished crust.",
      },
      {
        icon: "flame",
        title: "Sides and breads",
        body: "Veg pakora, samosa, paneer tikka, vegetable manchurian — plus naan, garlic naan, cheese naan, paratha.",
      },
    ],
    bodyHeading: "A full vegetarian Indian menu — not an afterthought.",
    body: `If you're vegetarian and you've ordered Indian food before, you already know the drill: most restaurants give you four or five default options and call it a menu. We do it differently. Roughly a third of our menu — 40-plus dishes — is fully vegetarian, and many of those can be made vegan by swapping cream for coconut cream (just ask in the order notes).

Our Paneer Butter Masala is a standout — house-made paneer cubes simmered in a tomato-cream gravy with butter and fenugreek. Daal Makhani is the slow-cooked black lentil and kidney bean curry we serve with butter and cream finished tableside. Chana Masala uses a Punjabi-style onion-tomato base with whole spices and a hint of tang.

On the pizza side, the Malai Veggie Pizza is the lighter herb-forward option, while Paneer Tikka brings smoky marinated paneer with peppers and red onion. Round it out with samosas, veg pakora, garlic naan, and a sweet lassi or masala chai.`,
  },

  "indian-food-delivery-lethbridge": {
    slug: "indian-food-delivery-lethbridge",
    path: "/indian-food-delivery-lethbridge",
    eyebrow: "Delivery · Lethbridge · Coaldale · Coalhurst",
    h1: "Indian food delivery in Lethbridge.",
    title: "Indian Food Delivery Lethbridge | Rivaaz — Pickup & In-House Delivery",
    description:
      "Indian food delivery in Lethbridge (T1H, T1J, T1K), Coaldale (T1M) and Coalhurst (T0L). No third-party markup. Order online from Rivaaz — curry, biryani, pizza, tandoori.",
    keywords:
      "Indian food delivery Lethbridge, food delivery Coaldale, food delivery Coalhurst, biryani delivery Lethbridge, butter chicken delivery, Rivaaz delivery",
    intro:
      "In-house delivery — no SkipTheDishes commission, no DoorDash markup. We charge a flat zone fee and bring the food ourselves, hot.",
    highlights: [
      {
        icon: "pin",
        title: "Three delivery zones",
        body: "Lethbridge (T1H/T1J/T1K) — $7, 35-60 min. Coaldale (T1M) — $17, 55-75 min. Coalhurst (T0L) — $17, 50-70 min. Spend $150+ and delivery is FREE anywhere we deliver.",
      },
      {
        icon: "clock",
        title: "Real-time quote",
        body: "Enter your postal code at checkout — we'll tell you the fee and ETA before you commit.",
      },
      {
        icon: "soup",
        title: "Full menu available",
        body: "149 dishes — curry, pizza, biryani, tandoori, breads, drinks, desserts. Combo Feasts work for delivery too.",
      },
    ],
    bodyHeading: "Why we deliver ourselves instead of using apps.",
    body: `Most local restaurants you order from on SkipTheDishes or DoorDash are paying 25-30% commission to the platform on every order. That cost has to come from somewhere — either the restaurant's already thin margin, or quietly bumped-up menu prices that you the customer pay without realizing.

At Rivaaz we decided to keep delivery in-house. When you order at rivaaz.ca, every dollar stays between you and us. The menu prices match what you'd pay if you walked in. Delivery is a flat zone fee — $7 for most of Lethbridge, $17 for Coaldale, $17 for Coalhurst. Order $150 or more of food and delivery is on the house, anywhere we deliver. No "service fees" or "small-order fees" hidden anywhere.

The trade-off: we deliver during our hours (every day 11 AM – 10 PM; online orders close 9:30 PM) and we use the customer's postal code at checkout to verify we can reach the address. Outside our three zones today? Pickup is always 25 minutes away, and we may expand zones based on demand — let us know in the order notes.`,
  },
};
